import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSession } from '@/auth/index';
import { AIPill } from '@/components/Shared/AIPill/AIPill';
import { useAppConfigContext } from '@/context/AppConfig';
import { useOram } from '@/myGarage/useOram';
import type { SearchReportResponseData } from '@/reports/searchReport';
import { vehicleTeaser } from '@/reports/shared/VendorData/VendorData';
import { vehicleSearch, SearchParams } from '@/reports/vehicle/vehicleSearch';
import {
  Grid,
  Text,
  Button,
  InputLabel,
  Input,
  Autocomplete,
  Stack,
  Box,
  CircularProgress,
} from '@/theme/index';
import { searchFormsRecyclingValidation } from '@/utils/index';
import { schemaPlateSearchForm } from '@/utils/schemaForms/schemaForms';
import { getAbbrStatesAsArray, isValidState } from '@/utils/states';
import { StyledUnderlineButton } from '../components';
import { VehicleSearchProps } from '../utils';

/**
 * This component is responsible for providing users with the ability to search for vehicles by License Plate.
 * Validate that the License Plate entered is valid and once the search is successful provide the necessary information to redirect to the report.
 */
export const LicensePlateSearchForm = ({
  onSuccess,
  onError,
  claimReport = false,
  clearFields,
  searchButtonText = 'Search',
  searchButtonSize,
  mobileView = false,
  optionalProps = {},
  placement = 'header',
  ddPlan = '',
}: VehicleSearchProps) => {
  const { session } = useSession();
  const { account } = session;
  const { logError, trackEventGA4 } = useAppConfigContext();
  const states = getAbbrStatesAsArray().filter((state) => state !== 'All');
  const queryClient = useQueryClient();
  const [isFetching, setIsFetching] = useState(false);

  const vehicleQuery = useMutation<
    SearchReportResponseData,
    Error,
    SearchParams
  >({
    mutationFn: vehicleSearch,
    onSuccess: (data) => {
      onSuccess &&
        onSuccess({
          permalink: data.report.permalink,
          reportType: 'vehicle',
          searchParams: '',
        });
    },
    onError: (error) => {
      logError('License Plate Vehicle Search', error);
      onError && onError(error);
    },
  });
  const {
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
    reset,
    setValue,
    setError,
    control,
  } = useForm<{
    licensePlate: string;
    state: string;
  }>({
    resolver: yupResolver(schemaPlateSearchForm),
    defaultValues: {
      licensePlate: '',
      state: '',
    },
  });

  const { createTrackedVehicleMutation } = useOram();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const { value } = event.target as HTMLInputElement;
      setValue('licensePlate', value);

      handleSubmit(onLicensePlateSearchVehicle)();
    }
  };

  const onLicensePlateSearchVehicle = async ({
    licensePlate,
    state,
  }: SearchParams) => {
    if (!searchFormsRecyclingValidation(account)) return;

    try {
      setIsFetching(true);
      if (!claimReport) {
        trackEventGA4({
          event_name: 'report_search',
          report_type: 'vehicle',
          search_by: 'license_plate',
          search_from: `${placement}_search`,
        });
        vehicleQuery.mutate({ licensePlate, state, ddPlan });
      } else {
        // We need to verify if the user has claimed the maximum number of vehicles but we need the remoulate service to do so.
        // Remoulate service is in progress within another ticket.
        const teaserResponse = await queryClient.fetchQuery(
          ['teaserRequest'],
          () => vehicleTeaser({ licensePlate, state })
        );
        const automobile = teaserResponse?.entities?.vehicles?.automobiles[0];
        const validVehicle =
          Boolean(automobile?.year) &&
          Boolean(automobile?.make) &&
          Boolean(automobile?.model);
        if (validVehicle) {
          vehicleQuery.mutate({
            licensePlate,
            state,
            tag: 'self',
            year: automobile.year,
            make: automobile.make,
            model: automobile.model,
            ddPlan,
          });

          createTrackedVehicleMutation.mutate({
            license_plate: licensePlate,
            state,
            year: automobile.year,
            make: automobile.make,
            model: automobile.model,
          });

          trackEventGA4({
            event_name: 'report_search',
            report_type: 'vehicle',
            search_by: 'license_plate',
            search_from: `${placement}_search`,
          });
        } else {
          setError('licensePlate', {
            type: 'custom',
            message: 'We could not find any information, please search again.',
          });
        }
      }
      setIsFetching(false);
    } catch (e: unknown) {
      setIsFetching(false);
      if (e instanceof Error) {
        logError('Vehicle teaser failed', e);
      }
      setError('licensePlate', {
        type: 'custom',
        message: 'Something went wrong, please search again.',
      });
    }
  };

  const onLicensePlateChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue('licensePlate', target.value.toUpperCase());
    clearErrors('licensePlate');
  };

  const onStateChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue('state', target.value.toUpperCase());
    clearErrors('state');
  };

  const mobileHeight = mobileView && placement === 'dashboard';

  return (
    <form
      data-testid="license-plate-search-form"
      data-cy="license-plate-search-form"
      onSubmit={handleSubmit(onLicensePlateSearchVehicle)}
      autoComplete="off"
      aria-label="form"
      style={optionalProps?.formStyle}
    >
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: {
            sm: `${
              optionalProps?.licensePlateLabelHidden ? 'start' : 'center'
            }`,
          },
          flexDirection: {
            xs: 'column',
            sm: `${mobileView ? 'column' : 'row'}`,
          },
        }}
      >
        <Grid
          container
          item
          spacing={2}
          sm={mobileView ? 12 : optionalProps?.licensePlateComponentsWidth ?? 6}
          sx={{
            flexDirection: {
              xs: 'column',
              sm: `${mobileView ? 'column' : 'row'}`,
            },
          }}
        >
          <Grid
            item
            sm={mobileView ? 12 : 9}
            sx={{
              height: mobileHeight
                ? 'auto'
                : optionalProps?.selectHeight ?? '105px',
              mb: { sm: 0 },
            }}
          >
            <Stack flexDirection="row" alignItems="center">
              <InputLabel
                htmlFor="plate"
                sx={{
                  fontWeight: 'bold',
                  ...(Boolean(optionalProps?.licensePlateLabelHidden) && {
                    display: 'none',
                  }),
                }}
              >
                License Plate
              </InputLabel>
              <Box sx={{ ml: 2, mb: 0.5 }}>
                <AIPill customStyle={{ height: '1.125rem' }} />
              </Box>
            </Stack>
            <Input
              {...register('licensePlate', { required: true })}
              fullWidth
              id="plate"
              type="text"
              name="plate"
              placeholder="License Plate Number"
              error={Boolean(errors.licensePlate?.message)}
              inputProps={{ maxLength: 10, onKeyDown: handleKeyDown }}
              onChange={onLicensePlateChange}
            />
            {errors.licensePlate?.message && (
              <Text variant="body2" color="error">
                {errors.licensePlate.message}
              </Text>
            )}
          </Grid>
          <Grid item sm={mobileView ? 12 : 3}>
            <InputLabel
              htmlFor="state"
              sx={{
                fontWeight: 'bold',
                ...(Boolean(optionalProps?.stateLabelHidden) && {
                  display: 'none',
                }),
              }}
            >
              State
            </InputLabel>
            <Controller
              control={control}
              name="state"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  {...register('state', { required: true })}
                  id="states-select"
                  options={states}
                  value={value}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      placeholder="State"
                      variant="outlined"
                      name="state"
                      error={Boolean(errors.state?.message)}
                      onInput={onStateChange}
                      onBlur={() => {
                        if (!isValidState(value)) {
                          onChange('');
                        }
                      }}
                    />
                  )}
                  onChange={(event, data) => onChange(data)}
                  isOptionEqualToValue={(option, value) =>
                    value === '' || option === value
                  }
                />
              )}
            />
            {errors.state?.message && (
              <Text variant="body2" color="error">
                {errors.state.message}
              </Text>
            )}
            {mobileView && clearFields && (
              <StyledUnderlineButton
                onClick={() => {
                  reset();
                }}
              >
                Clear field(s)
              </StyledUnderlineButton>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          width={{ xs: '100%', md: searchButtonSize }}
          sx={{ mb: 0.5 }}
        >
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="success"
            startIcon={optionalProps?.searchStartIcon}
          >
            {isFetching ? (
              <CircularProgress
                size="2rem"
                sx={(theme) => ({ color: theme.palette.success.contrastText })}
              />
            ) : (
              searchButtonText
            )}
          </Button>
        </Grid>
      </Grid>
      {!mobileView && clearFields && (
        <StyledUnderlineButton
          onClick={() => {
            reset();
          }}
        >
          Clear field(s)
        </StyledUnderlineButton>
      )}
    </form>
  );
};
